import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import type { JiraCreateItemsData } from '@atlassian/rovo-content-bridge-api-commands/commands/jira-create-items-command';

import { fieldTypeToPayload } from '../../common/constants';
import type { JiraBulkCreateApiResponse, JiraBulkCreateResult } from '../../common/types';
import type { JiraContextData } from '../../controllers/use-ai-create-project-context/types';

const isValidJiraId = (keyOrId: string): boolean => Number.isInteger(Number(keyOrId));

const BASE_URL_REGEX = /^(https:\/\/[^\/]+)/;

// Jira Bulk Create API
const getBulkCreateUrl = (cloudId: string) =>
	`/gateway/api/ex/jira/${cloudId}/rest/api/3/issue/bulk`;

// Extract cloudUrl from the first issue
const extractCloudUrl = (issues: JiraBulkCreateApiResponse['issues']): string => {
	return issues[0]?.self.match(BASE_URL_REGEX)?.[0] ?? '';
};

/**
 * Fetches the Jira Bulk Create API and returns the result
 * If an empty array of issues is returned, then we will use the default error message
 * handled by the upstream
 *
 * @param draftWorkItems - The draft work items to create
 */
export const createIssuesInBulk = async ({
	draftWorkItems,
	cloudId,
}: {
	draftWorkItems: JiraContextData[];
	cloudId: string;
}): Promise<JiraBulkCreateResult> => {
	try {
		const url = getBulkCreateUrl(cloudId);

		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			body: JSON.stringify(buildBulkCreateRequest(draftWorkItems)),
		});
		const data = (await response.json()) as JiraBulkCreateApiResponse;
		if (data.issues.length === 0 || data.errors.length > 0) {
			// Get unique errors that appear across all issues in the bulk create request
			const uniqueErrors: Set<string> = new Set(
				data.errors.flatMap((e) => {
					if (Object.values(e.elementErrors.errors).length > 0) {
						return Object.values(e.elementErrors.errors);
					}
					return e.elementErrors.errorMessages;
				}),
			);
			// Convert each error into sentence case
			return {
				isSuccess: false,
				errorMessages: Array.from(uniqueErrors),
			};
		}
		return {
			isSuccess: true,
			createdIssues: data.issues,
			cloudUrl: extractCloudUrl(data.issues),
		};
	} catch (err) {
		logException({
			exception: err,
			name: 'CreateParentIssueBulkError',
		});

		// Return an empty array of error messages to indicate that the error is not related to the bulk create API
		return {
			isSuccess: false,
			errorMessages: [],
		};
	}
};

// Exported for testing purposes
export const buildBulkCreateRequest = (
	draftWorkItems: JiraCreateItemsData[],
): {
	issueUpdates: {
		fields:
			| {
					parent:
						| {
								id: string;
								key?: undefined;
						  }
						| {
								key: string;
								id?: undefined;
						  };
					issuetype: {
						id: string;
					};
					project:
						| {
								id: string;
								key?: undefined;
						  }
						| {
								key: string;
								id?: undefined;
						  };
			  }
			| {
					parent?: undefined;
					issuetype: {
						id: string;
					};
					project:
						| {
								id: string;
								key?: undefined;
						  }
						| {
								key: string;
								id?: undefined;
						  };
			  };
		update?: Record<string, unknown>;
	}[];
} => ({
	issueUpdates: draftWorkItems.map((draftWorkItem) => {
		const { projectIdOrKey, issueTypeId } = draftWorkItem;

		// Ensure parent and project fields have the correct field key
		const parentIdOrKey = draftWorkItem.fields?.parent?.id;
		const parentField = parentIdOrKey
			? { parent: isValidJiraId(parentIdOrKey) ? { id: parentIdOrKey } : { key: parentIdOrKey } }
			: {};

		const projectField = isValidJiraId(projectIdOrKey)
			? { id: projectIdOrKey }
			: { key: projectIdOrKey };

		// Update-operation fields (e.g. issuelinks, comment) are not accepted under
		// `fields` by the Jira bulk create API and must be sent under `update` as an
		// array of operation objects (e.g. `[{ add: <value> }]`).
		const fieldsToCreate: Record<string, unknown> = {};
		const fieldsToUpdate: Record<string, unknown> = {};

		for (const [fieldKey, fieldValue] of Object.entries(draftWorkItem.fields ?? {})) {
			if (fieldTypeToPayload[fieldKey]?.isUpdateOperation) {
				fieldsToUpdate[fieldKey] = fieldValue;
			} else {
				fieldsToCreate[fieldKey] = fieldValue;
			}
		}

		return {
			fields: {
				...fieldsToCreate,
				issuetype: { id: issueTypeId },
				project: projectField,
				...parentField,
			},
			update: fieldsToUpdate,
		};
	}),
});
